import { useAskInputState } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { countFileDropItems, filesFromDropItems, } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-upload/dropFiles.js';
import { isFileSystemDirectoryEntry } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { getPastedTextFileName } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/getPastedTextFileName.js';
import { useFileUploadAnalytics } from '#pplx/frontend/components/hooks/fileUploadAnalytics.js';
import { MAX_CHARACTERS_ALLOWED_TO_SUBMIT } from '#pplx/frontend/components/utils/consts.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
// Bound synchronous rejection fanout because each candidate emits a started/completed pair.
// Real paste/drop interactions are normally single-digit; beyond 10, the full selected count
// preserves interaction size while per-file size/extension mix adds little analytics signal.
const MAX_REJECTED_UPLOAD_CANDIDATES_PER_INTERACTION = 10;
const renameWithIndex = (blob, index) => {
    const dotIndex = blob.name.lastIndexOf('.');
    const baseName = dotIndex > 0 ? blob.name.slice(0, dotIndex) : blob.name;
    const ext = dotIndex > 0 ? blob.name.slice(dotIndex) : '';
    const fileName = index === 1 ? `${baseName}${ext}` : `${baseName}-${index}${ext}`;
    return new File([blob], fileName, { type: blob.type });
};
export const useAskInputFileUploadCompletion = ({ focusInput, focusOnFileAttachComplete = true, onFileAttachComplete, setAttachments, setIsUploadingFile, }) => {
    const { activeMenu } = useAskInputState();
    return useCallback((attachmentResults) => {
        setIsUploadingFile(false);
        if (attachmentResults.length > 0) {
            setAttachments(attachmentResults);
            // Do not steal focus from an open menu: focusing closes the active menu.
            if (focusOnFileAttachComplete && activeMenu === null) {
                focusInput();
            }
        }
        else {
            setAttachments(undefined);
        }
        onFileAttachComplete?.(attachmentResults);
    }, [
        activeMenu,
        focusInput,
        focusOnFileAttachComplete,
        onFileAttachComplete,
        setAttachments,
        setIsUploadingFile,
    ]);
};
export const useAskInputFileAndPasteHandling = ({ fileUploadRef, fileHandlingRef, showFileUpload, attachments, onFileUploadHidden, handleChange, gateUploadFile, maxFiles, preserveFileIdentity = false, }) => {
    const { inputRef } = useAskInputState();
    const { rejectInitiation } = useFileUploadAnalytics('thread_attachment');
    const { track } = useInstrumentation();
    // Track auto-created text files for notification display
    const [autoCreatedTextFile, setAutoCreatedTextFile] = useState(null);
    const createTextFile = useCallback((text) => {
        const fileName = getPastedTextFileName(attachments?.length ?? 0);
        const file = new File([text], fileName, {
            type: 'text/plain',
        });
        const fileList = new DataTransfer();
        fileList.items.add(file);
        // Track the auto-created file for notification
        setAutoCreatedTextFile({ content: text, fileName });
        // Don't clear the input - preserve existing text
        fileUploadRef.current?.uploadFiles(fileList.files);
    }, [attachments, fileUploadRef]);
    const dismissNotification = useCallback(() => {
        setAutoCreatedTextFile(null);
    }, []);
    // Expose imperative handle through ref
    useImperativeHandle(fileHandlingRef, () => ({
        clearAutoCreatedTextFile: dismissNotification,
    }), [dismissNotification]);
    const handlePaste = useCallback((e) => {
        const clipboardData = e.clipboardData;
        if (clipboardData && clipboardData.items.length > 0) {
            const clipboardItems = Array.from(clipboardData.items);
            const pastedText = clipboardData.getData('Text');
            const uploadCandidateCount = clipboardItems.filter((item) => item.kind === 'file' ||
                (item.type === 'text/plain' && pastedText.length > MAX_CHARACTERS_ALLOWED_TO_SUBMIT)).length;
            const gateError = uploadCandidateCount > 0 ? gateUploadFile() : null;
            if (gateError) {
                const rejectedFiles = clipboardItems.flatMap((item) => {
                    if (item.kind === 'file') {
                        const file = item.getAsFile();
                        return file ? [{ size: file.size }] : [];
                    }
                    // paste.txt is a synthetic name for the oversized-paste rejection event only.
                    // The analytics builder reads only the `.txt` suffix (allowlisted) and size; no other
                    // rejection logic consumes the name. The accepted path uses createTextFile with its own timestamped name.
                    return item.type === 'text/plain' &&
                        pastedText.length > MAX_CHARACTERS_ALLOWED_TO_SUBMIT
                        ? [{ name: 'paste.txt', size: new Blob([pastedText]).size }]
                        : [];
                });
                rejectInitiation(rejectedFiles.slice(0, MAX_REJECTED_UPLOAD_CANDIDATES_PER_INTERACTION), 'local', gateError, uploadCandidateCount).forEach(track);
                // Prevent the browser from inserting a gated file or oversized text into the composer.
                e.preventDefault();
                return;
            }
            const fileList = new DataTransfer();
            let fileIndex = (attachments?.length ?? 0) + 1;
            for (const item of clipboardData.items) {
                if (item.kind === 'file') {
                    // Prevent default paste insertion — file is handled as an attachment instead
                    e.preventDefault();
                    const blob = item.getAsFile();
                    if (blob) {
                        fileList.items.add(preserveFileIdentity ? blob : renameWithIndex(blob, fileIndex));
                        fileIndex++;
                    }
                }
                if (item.type === 'text/plain') {
                    const pastedData = e.clipboardData.getData('Text');
                    if (pastedData.length > MAX_CHARACTERS_ALLOWED_TO_SUBMIT) {
                        // Prevent default paste insertion — oversized text is converted to a file attachment
                        e.preventDefault();
                        createTextFile(pastedData);
                    }
                }
            }
            fileUploadRef.current?.uploadFiles(fileList.files);
        }
        window.requestAnimationFrame(() => {
            if (!inputRef.current) {
                return;
            }
            // Always read from the input for a manual `handleChange` less we
            // lose rich text @-mentions
            handleChange(inputRef.current.value, inputRef.current.json);
        });
    }, [
        fileUploadRef,
        handleChange,
        inputRef,
        createTextFile,
        gateUploadFile,
        attachments,
        rejectInitiation,
        track,
        preserveFileIdentity,
    ]);
    const handlePasteQuery = useCallback((selectedQuery) => {
        // Though we normally always read from the input for a manual `handleChange`
        // this is an exception as we want to overwrite the entire input
        handleChange(selectedQuery);
        inputRef.current?.focus();
    }, [handleChange, inputRef]);
    const asyncSelectionGeneration = useRef(0);
    useEffect(() => {
        // Only unmount invalidates in-flight normalization. Callback identity changes between
        // renders must not cancel work started with the initiating render's captured values.
        return () => {
            asyncSelectionGeneration.current += 1;
        };
    }, []);
    const handleFileSelect = useCallback(async (items) => {
        const selectionGeneration = asyncSelectionGeneration.current;
        const selectionIsActive = () => asyncSelectionGeneration.current === selectionGeneration;
        const itemArray = Array.from(items);
        const fileItemCount = countFileDropItems(items);
        const directFiles = new Set();
        const normalizedItems = itemArray.map((item) => {
            if (item.kind !== 'file') {
                return item;
            }
            const entry = item.webkitGetAsEntry?.() ?? null;
            if (entry !== null && isFileSystemDirectoryEntry(entry)) {
                return item;
            }
            const file = item.getAsFile();
            if (file === null) {
                return item;
            }
            directFiles.add(file);
            return {
                kind: 'file',
                getAsFile: () => file,
                webkitGetAsEntry: () => entry,
            };
        });
        const textPromises = itemArray
            .filter((item) => item.kind === 'string' && item.type === 'text/plain')
            .map((item) => new Promise((resolve) => {
            item.getAsString(resolve);
        }));
        const droppedFiles = await filesFromDropItems(normalizedItems, maxFiles);
        if (!selectionIsActive()) {
            return;
        }
        const droppedTexts = await Promise.all(textPromises);
        if (!selectionIsActive()) {
            return;
        }
        const selectionFileCount = Math.max(fileItemCount, droppedFiles.length);
        if (selectionFileCount > 0 && selectionFileCount + (attachments?.length ?? 0) > maxFiles) {
            const fileList = new DataTransfer();
            droppedFiles.forEach((file) => fileList.items.add(file));
            fileUploadRef.current?.uploadFiles(fileList.files, selectionFileCount);
            return;
        }
        const gateError = gateUploadFile();
        if (gateError) {
            const rejectedFiles = droppedFiles.map((file) => ({ size: file.size }));
            rejectInitiation(rejectedFiles.length > 0 ? rejectedFiles : undefined, 'local', gateError, selectionFileCount || itemArray.length).forEach(track);
            return;
        }
        const fileList = new DataTransfer();
        let fileIndex = (attachments?.length ?? 0) + 1;
        for (const file of droppedFiles) {
            if (directFiles.has(file) && !preserveFileIdentity) {
                fileList.items.add(renameWithIndex(file, fileIndex));
                fileIndex++;
            }
            else {
                fileList.items.add(file);
            }
        }
        droppedTexts.forEach((text) => {
            if (text.length > MAX_CHARACTERS_ALLOWED_TO_SUBMIT) {
                createTextFile(text);
            }
            else if (inputRef.current) {
                // Append to the input and read from it for a manual `handleChange`
                // less we lose @-mentions
                inputRef.current.append(text);
                handleChange(inputRef.current.value, inputRef.current.json);
            }
        });
        fileUploadRef.current?.uploadFiles(fileList.files);
    }, [
        fileUploadRef,
        inputRef,
        handleChange,
        maxFiles,
        createTextFile,
        gateUploadFile,
        attachments,
        rejectInitiation,
        track,
        preserveFileIdentity,
    ]);
    useEffect(() => {
        if (!showFileUpload) {
            onFileUploadHidden?.();
            setAutoCreatedTextFile(null);
        }
    }, [onFileUploadHidden, showFileUpload]);
    return useMemo(() => ({
        handlePaste,
        handlePasteQuery,
        handleFileSelect,
        autoCreatedTextFile,
        dismissNotification,
        setAutoCreatedTextFile,
    }), [handleFileSelect, handlePaste, handlePasteQuery, autoCreatedTextFile, dismissNotification]);
};
//# sourceMappingURL=useAskInputFileAndPasteHandling.js.map